<template>
  <div class="sign-page">

    <!-- Üst çubuk -->
    <header class="topbar">
      <a class="brand" :href="appUrl">
        <img src="../assets/logo2.png" :alt="brand" @error="logoFailed = true" v-if="!logoFailed">
        <span v-else>{{ brand }}</span>
      </a>

      <div class="top-mid" v-if="ready">
        <strong class="top-subject">{{ subject }}</strong>
        <span class="top-progress" :class="{ 'is-done': !remaining }">
          <i class="fa" :class="remaining ? 'fa-circle-o' : 'fa-check-circle'"></i>
          {{ remaining ? $t('sign.fieldsLeft', { count: remaining }) : $t('sign.allDone') }}
        </span>
      </div>

      <div class="top-right">
        <button class="btn-link" v-if="ready" @click="declineOpen = true">{{ $t('sign.decline') }}</button>
        <LanguageSwitcher />
      </div>
    </header>

    <!-- Durum ekranları -->
    <div class="state-wrap" v-if="loading || blockedMessage || done">
      <div class="state" v-if="loading">
        <span class="state-icon is-idle"><i class="fa fa-circle-o-notch fa-spin"></i></span>
        <p>{{ $t('common.loading') }}</p>
      </div>
      <div class="state" v-else-if="blockedMessage">
        <span class="state-icon is-idle"><i class="fa fa-lock"></i></span>
        <h2>{{ blockedMessage }}</h2>
        <p>{{ $t('sign.blockedHint') }}</p>
      </div>
      <div class="state" v-else>
        <span class="state-icon is-ok"><i class="fa fa-check"></i></span>
        <h2>{{ doneMessage }}</h2>
        <p>{{ $t('sign.doneHint') }}</p>
      </div>
    </div>

    <!-- Belge: tam sayfa -->
    <main class="viewer" ref="viewer" v-else>

      <div class="flash is-err" v-if="errorMessage">
        <i class="fa fa-exclamation-circle"></i>
        <span>{{ errorMessage }}</span>
        <button class="flash-close" @click="errorMessage = ''">&times;</button>
      </div>

      <div class="stage">

        <!-- Sol kenar: rehber etiketi. Belgeyle birlikte kayıyor ve sıradaki
             alanın hizasına geliyor. -->
        <div class="rail">
          <button class="guide" :class="{ 'is-finish': !remaining }"
                  :style="{ top: guideTop + 'px' }" @click="guide">
            {{ guideLabel }}
          </button>
        </div>

        <div class="pages" ref="pages">
          <p class="doc-note" v-if="message">{{ message }}</p>

          <div class="doc-loading" v-if="docLoading">
            <i class="fa fa-circle-o-notch fa-spin"></i> {{ $t('common.loading') }}
          </div>
          <p class="doc-error" v-else-if="docError">{{ docError }}</p>

          <div v-for="(page, index) in pages" :key="index" class="page"
               :ref="'page' + index" :style="{ paddingTop: (page.ratio * 100) + '%' }">
            <canvas class="page-canvas" :ref="'canvas' + index"></canvas>

            <!-- Bu imzalayanın alanları, sayfanın üstünde -->
            <div v-for="f in fieldsOnPage(index + 1)" :key="f.key"
                 class="field" :class="fieldClass(f)" :style="boxStyle(f)"
                 :ref="'field_' + f.key">

              <!-- İmza / paraf -->
              <template v-if="isSignature(f)">
                <!-- İmza atıldıktan sonra yatay sürüklenebiliyor. Kısa tıklama
                     eskisi gibi imzayı değiştirme penceresini açıyor. -->
                <div v-if="signatureImage" class="field-sig-wrap" :title="$t('sign.dragHint')"
                     @mousedown.prevent="startMove($event, f)"
                     @touchstart.prevent="startMove($event, f)">
                  <img :src="signatureImage" class="field-sig" draggable="false" alt="signature">
                  <span class="move-hint">
                    <i class="fa" :class="signatureMove.axis === 'xy' ? 'fa-arrows' : 'fa-arrows-h'"></i>
                  </span>
                </div>
                <button v-else class="sign-btn" @click="openAdopt(f)">
                  <i class="fa fa-pencil"></i>
                  {{ f.type === 'initials' ? $t('sign.initialHere') : $t('sign.signHere') }}
                </button>
              </template>

              <!-- Onay kutusu -->
              <label v-else-if="f.type === 'checkbox'" class="field-check">
                <input type="checkbox" v-model="values[f.key]" @change="touched(f)">
              </label>

              <!-- Liste -->
              <select v-else-if="f.type === 'dropdown'" class="field-input"
                      v-model="values[f.key]" @change="touched(f)">
                <option value="">{{ f.label }}</option>
                <option v-for="opt in f.options" :value="opt" :key="opt">{{ opt }}</option>
              </select>

              <!-- Metin, tarih, sayı, e-posta -->
              <input v-else class="field-input" :type="inputType(f.type)"
                     v-model="values[f.key]" :placeholder="f.label" @input="touched(f)">
            </div>
          </div>
        </div>
      </div>
    </main>

    <!-- Alt çubuk: onay ve gönder -->
    <footer class="bar" v-if="ready">
      <label class="consent">
        <input type="checkbox" v-model="consent">
        <span class="switch-box"></span>
        <span class="consent-text">{{ $t('sign.consent') }}</span>
      </label>
      <!-- Onay verilmeden gri: tıklanınca eksik alana / onaya yönlendiriyor -->
      <button class="btn-primary btn-deactive" @click="guide" v-if="!consent">
        <i class="fa fa-crosshairs"></i>
        <span>{{ $t('sign.submit') }}</span>
      </button>
      <button class="btn-primary" :class="{ 'is-loading': saving }" @click="submit" v-else>
        <i class="fa fa-check"></i>
        <span>{{ $t('sign.submit') }}</span>
      </button>
    </footer>

    <!-- İmza penceresi -->
    <div class="sheet" v-if="adoptOpen">
      <div class="sheet-backdrop" @click="closeAdopt"></div>
      <div class="sheet-card" role="dialog">
        <header class="sheet-head">
          <span class="sheet-icon"><i class="fa fa-pencil"></i></span>
          <div class="sheet-head-text">
            <h3>{{ $t('sign.adoptTitle') }}</h3>
            <p>{{ $t('sign.adoptLead') }}</p>
          </div>
          <button class="sheet-close" @click="closeAdopt" aria-label="close">&times;</button>
        </header>

        <div class="sheet-body">
          <div class="seg">
            <button v-if="savedSignature" :class="{ 'is-active': mode === 'saved' }"
                    @click="setMode('saved')">{{ $t('sign.tabSaved') }}</button>
            <button :class="{ 'is-active': mode === 'drawn' }"
                    @click="setMode('drawn')">{{ $t('sign.tabDraw') }}</button>
            <button :class="{ 'is-active': mode === 'typed' }"
                    @click="setMode('typed')">{{ $t('sign.tabType') }}</button>
            <button :class="{ 'is-active': mode === 'uploaded' }"
                    @click="setMode('uploaded')">{{ $t('sign.tabUpload') }}</button>
            <button :class="{ 'is-active': mode === 'pad' }"
                    @click="setMode('pad')">{{ $t('sign.tabPad') }}</button>
            <!-- Simülatör sekmesi yalnızca test ortamında; ortamı sunucu bildiriyor -->
            <button v-if="testMode" :class="{ 'is-active': mode === 'padmock' }"
                    @click="setMode('padmock')">{{ $t('sign.tabPadMock') }}</button>
          </div>

          <div v-show="mode === 'saved'" v-if="savedSignature">
            <div class="sig-paper">
              <img :src="savedSignature" alt="signature">
              <span class="sig-rule"></span>
            </div>
            <p class="hint center">{{ $t('sign.savedHint') }}</p>
          </div>

          <div v-show="mode === 'drawn'">
            <div class="sig-paper is-pad">
              <canvas ref="pad" class="pad"
                      @mousedown="startStroke" @mousemove="moveStroke" @mouseup="endStroke"
                      @mouseleave="endStroke"
                      @touchstart.prevent="startStroke" @touchmove.prevent="moveStroke"
                      @touchend.prevent="endStroke"></canvas>
              <span class="sig-rule"></span>
            </div>
            <div class="sig-actions">
              <p class="hint">{{ $t('sign.drawHint') }}</p>
              <button class="btn-ghost is-mini" @click="clearSignature">{{ $t('sign.clear') }}</button>
            </div>
          </div>

          <div v-show="mode === 'typed'">
            <input class="input center-text" v-model="typedName"
                   :placeholder="$t('sign.typePlaceholder')" @input="renderTyped">
            <div class="sig-paper is-pad">
              <canvas ref="typedPad" class="pad is-typed"></canvas>
              <span class="sig-rule"></span>
            </div>
          </div>

          <!-- Yükleme -->
          <div v-show="mode === 'uploaded'">
            <div class="drop" :class="{ 'has-file': !!uploadedImage }" @click="$refs.sigFile.click()">
              <input class="hidden-input" type="file" accept="image/png,image/jpeg"
                     ref="sigFile" @change="onSignatureFile">
              <img v-if="uploadedImage" :src="uploadedImage" alt="signature" class="drop-preview">
              <template v-else>
                <span class="drop-icon"><i class="fa fa-cloud-upload"></i></span>
                <p class="drop-text">{{ $t('sign.uploadPick') }}</p>
              </template>
            </div>
            <p class="hint center">{{ $t('sign.uploadHint') }}</p>
          </div>

          <!-- İmza tableti: yüz yüze kanal -->
          <div v-show="isPadMode">
            <div class="pad-status" :class="'is-' + padState">
              <i class="fa" :class="padIcon"></i>
              <span>{{ padMessage }}</span>
              <button class="btn-ghost is-mini" v-if="padState === 'error'" @click="connectPad">
                {{ $t('sign.padRetry') }}
              </button>
            </div>

            <!-- Tabletteki mürekkep bu tuvale yansıyor -->
            <div class="sig-paper is-pad" v-show="padState === 'ready'">
              <canvas ref="padCanvas" class="pad is-hardware" width="600" height="180"></canvas>
              <span class="sig-rule"></span>
            </div>
            <!-- İmzalayan neyin kaydedildiğini bilmeli: rıza meselesi -->
            <p class="note is-info" v-if="padState === 'ready' && biometricCapture">
              <i class="fa fa-info-circle"></i>
              {{ $t('sign.biometricNotice') }}
            </p>

            <div class="sig-actions" v-if="padState === 'ready'">
              <p class="hint">{{ padDevice }}</p>
              <button class="btn-ghost is-mini" @click="clearPad">{{ $t('sign.padClear') }}</button>
            </div>
          </div>

          <p class="err" v-if="adoptError">
            <i class="fa fa-exclamation-circle"></i> {{ adoptError }}
          </p>
        </div>

        <footer class="sheet-foot">
          <button class="btn-primary" @click="adopt">
            <i class="fa fa-check"></i>
            <span>{{ $t('sign.adoptButton') }}</span>
          </button>
          <button class="btn-ghost" @click="closeAdopt">{{ $t('common.cancel') }}</button>
        </footer>
      </div>
    </div>

    <!-- Reddetme -->
    <div class="sheet" v-if="declineOpen">
      <div class="sheet-backdrop" @click="declineOpen = false"></div>
      <div class="sheet-card" role="dialog">
        <header class="sheet-head">
          <span class="sheet-icon is-no"><i class="fa fa-times"></i></span>
          <div class="sheet-head-text">
            <h3>{{ $t('sign.declineTitle') }}</h3>
            <p>{{ $t('sign.declineLead') }}</p>
          </div>
          <button class="sheet-close" @click="declineOpen = false" aria-label="close">&times;</button>
        </header>
        <div class="sheet-body">
          <label class="label">{{ $t('sign.declineReason') }}</label>
          <textarea class="input is-area" rows="3" v-model="declineReason"
                    :placeholder="$t('sign.declineReasonPlaceholder')"></textarea>
        </div>
        <footer class="sheet-foot">
          <button class="btn-danger" :class="{ 'is-loading': saving }" @click="decline">
            {{ $t('sign.declineSubmit') }}
          </button>
          <button class="btn-ghost" @click="declineOpen = false">{{ $t('common.cancel') }}</button>
        </footer>
      </div>
    </div>

    <footer class="page-foot" v-if="loading || blockedMessage || done">
      <span>{{ $t('sign.footerNote', { brand: brand }) }}</span>
      <a :href="appUrl" target="_blank" rel="noopener">{{ $t('sign.footerLink') }} &rarr;</a>
    </footer>
  </div>
</template>

<script>
import api from '../config/api';
import LanguageSwitcher from './LanguageSwitcher.vue';
import signaturePad from '../config/signaturePad';
import biometricSeal from '../config/biometricSeal';

const PDFJS_URL = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
const PDFJS_WORKER = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';

let pdfjsPromise = null;
function loadPdfJs() {
  if (window.pdfjsLib) return Promise.resolve(window.pdfjsLib);
  if (pdfjsPromise) return pdfjsPromise;
  pdfjsPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = PDFJS_URL;
    script.onload = () => {
      window.pdfjsLib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER;
      resolve(window.pdfjsLib);
    };
    script.onerror = () => reject(new Error('pdf.js could not be loaded'));
    document.head.appendChild(script);
  });
  return pdfjsPromise;
}

// Blob'u data URL'e çevirir; sunucu imzayı bu biçimde bekliyor.
function readBlobAsDataUrl(blob) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => resolve('');
    reader.readAsDataURL(blob);
  });
}

// Hata cevabı JSON olarak gelse de blob tipinde oluyor
function readBlobAsText(blob) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => resolve('');
    reader.readAsText(blob);
  });
}

export default {
  name: 'Sign',
  components: { LanguageSwitcher },
  props: {
    signToken: { type: String, default: '' },
    brand: { type: String, default: 'DocuLock' },
    appUrl: {
      type: String,
      default: function () {
        try { return window.location.origin + '/'; } catch (e) { return '/'; }
      }
    }
  },
  data() {
    return {
      token: this.signToken
        || (this.$route && this.$route.params ? this.$route.params.token : '')
        || (window.location.hash.match(/#\/sign\/([A-Za-z0-9]+)/) || [])[1]
        || '',
      logoFailed: false,

      loading: true,
      saving: false,
      blockedMessage: '',
      errorMessage: '',
      done: false,
      doneMessage: '',

      subject: '',
      message: '',
      recipient: {},
      document: { fields: [] },
      values: {},

      // Belge görüntüleme
      pdf: null,
      pages: [],
      docLoading: true,
      docError: '',

      // İmza kutularının yatay konumu (alan anahtarı -> x oranı).
      // Yalnızca taşınanlar burada; sunucuya da yalnızca bunlar gidiyor.
      offsets: {},
      moving: null,
      // Taşıma kuralı sunucudan geliyor; gelmezse en kısıtlı hali
      signatureMove: { axis: 'x', shift: 0.3 },

      // Rehber
      guideTop: 16,
      started: false,

      // İmza
      signatureImage: '',
      signatureType: 'drawn',
      signatureDevice: '',
      adoptOpen: false,
      adoptError: '',
      mode: 'drawn',
      typedName: '',
      isDrawing: false,
      hasStroke: false,
      ctx: null,
      savedSignature: '',

      // Yüklenen imza (işlenmiş, saydam zeminli PNG)
      uploadedImage: '',

      // Sunucu test ortamında olduğumuzu bildirirse simülatör sekmesi çıkıyor
      testMode: false,

      // İmza tableti
      padState: 'idle',        // idle | connecting | ready | error
      padError: '',
      padDevice: '',
      padCapture: null,        // tabletten toplanan ham veriler (konsola da yazılıyor)
      biometricCapture: false, // sunucu biyometrik toplamayı açtıysa true
      biometricKey: '',        // kurumun açık anahtarı (şifreleme cihazda)
      biometricSealed: null,   // mühürlenmiş paket; sunucuya bu gidiyor

      consent: false,
      declineOpen: false,
      declineReason: ''
    };
  },
  computed: {
    ready() {
      return !this.loading && !this.blockedMessage && !this.done;
    },
    signedIn() {
      const app = this.$store ? this.$store.state.the_app_data : null;
      return !!(app && app.token);
    },
    fields() {
      return this.document.fields || [];
    },
    // Rehberin sırası: sayfa, sonra yukarıdan aşağı, sonra soldan sağa
    orderedFields() {
      return this.fields.slice().sort(function (a, b) {
        return (a.page - b.page) || (a.y - b.y) || (a.x - b.x);
      });
    },
    pendingFields() {
      return this.orderedFields.filter((f) => f.required !== false && !this.isFilled(f));
    },
    remaining() {
      return this.pendingFields.length;
    },
    // Başlamadan önce belgenin başında "Başla"; sonra sıradaki alanın
    // hizasında, alanın türüne göre "İmzala", "Paraf" ya da "Doldur"
    isPadMode() {
      return this.mode === 'pad' || this.mode === 'padmock';
    },
    // Bağdaştırıcıya hangi kaynağı açacağını söylüyoruz
    padKind() {
      return this.mode === 'padmock' ? 'mock' : 'real';
    },

    padMessage() {
      if (this.padState === 'connecting') return this.$t('sign.padConnecting');
      if (this.padState === 'ready') return this.$t('sign.padReady');
      if (this.padState === 'error') {
        return this.padError === 'script' ? this.$t('sign.padNoScript') : this.$t('sign.padNoService');
      }
      return '';
    },
    padIcon() {
      return {
        connecting: 'fa-circle-o-notch fa-spin',
        ready: 'fa-check-circle',
        error: 'fa-exclamation-circle'
      }[this.padState] || 'fa-circle-o';
    },

    guideLabel() {
      if (!this.remaining) return this.$t('sign.guideFinish');
      if (!this.started) return this.$t('sign.guideStart');
      var next = this.pendingFields[0];
      if (next.type === 'signature') return this.$t('sign.signHere');
      if (next.type === 'initials') return this.$t('sign.initialHere');
      return this.$t('sign.guideFill');
    }
  },
  mounted() {
    if (!this.token) {
      this.blockedMessage = this.$t('sign.missingToken');
      this.loading = false;
      return;
    }
    this.load();
    window.addEventListener('resize', this.onResize);
  },
  beforeDestroy() {
    this.endMove();
    window.removeEventListener('resize', this.onResize);
    signaturePad.stop();
  },
  methods: {
    // --- Yükleme -----------------------------------------------------------

    load() {
      api.get('/sign/' + this.token)
        .then((body) => {
          this.subject = body.subject;
          this.message = body.message;
          this.recipient = body.recipient;
          if (body.locale) this.$setLocale(body.locale);
          this.document = body.document || { fields: [] };
          if (body.signatureMove) this.signatureMove = body.signatureMove;
          this.testMode = !!body.testMode;
          this.biometricCapture = !!body.biometricCapture;
          this.biometricKey = body.biometricPublicKey || '';
          this.typedName = body.recipient.name;

          const incoming = body.values || {};
          this.fields.forEach((f) => {
            this.$set(this.values, f.key, incoming[f.key] || (f.type === 'checkbox' ? false : ''));
          });

          if (this.signedIn) this.loadSavedSignature();
        })
        .catch((err) => { this.blockedMessage = err.message; })
        .then(() => {
          this.loading = false;
          if (!this.blockedMessage) this.$nextTick(() => this.loadDocument());
        });
    },

    // Belgeyi PDF olarak alıp sayfaları kendimiz çiziyoruz: alanları
    // tam koordinatına yerleştirebilmek için iframe yetmiyor
    loadDocument() {
      this.docLoading = true;
      api.get('/sign/' + this.token + '/document', { responseType: 'blob' })
        .then((blob) => {
          if (blob.type && blob.type.indexOf('application/json') > -1) {
            return readBlobAsText(blob).then((text) => {
              let message = this.$t('sign.docUnavailable');
              try { message = JSON.parse(text).message || message; } catch (e) { /* JSON değil */ }
              throw new Error(message);
            });
          }
          return blob.arrayBuffer();
        })
        .then((buffer) => loadPdfJs().then((pdfjs) => pdfjs.getDocument({ data: buffer }).promise))
        .then((pdf) => {
          this.pdf = pdf;
          const pages = [];
          for (let i = 1; i <= pdf.numPages; i++) pages.push({ ratio: 1.414 });
          this.pages = pages;
          this.docLoading = false;
          this.$nextTick(() => this.renderPages());
        })
        .catch((err) => {
          this.docError = err.message;
          this.docLoading = false;
        });
    },

    renderPages() {
      if (!this.pdf) return;
      for (let i = 1; i <= this.pdf.numPages; i++) this.renderPage(i);
    },

    renderPage(pageNumber) {
      this.pdf.getPage(pageNumber).then((page) => {
        const index = pageNumber - 1;
        const unscaled = page.getViewport({ scale: 1 });
        this.$set(this.pages, index, { ratio: unscaled.height / unscaled.width });

        this.$nextTick(() => {
          const holder = this.refOf('page' + index);
          const canvas = this.refOf('canvas' + index);
          if (!holder || !canvas) return;

          // Ekran yoğunluğuna göre çiz: yoksa yazı bulanık görünüyor
          const ratio = window.devicePixelRatio || 1;
          const width = holder.clientWidth;
          const viewport = page.getViewport({ scale: (width / unscaled.width) * ratio });
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          page.render({ canvasContext: canvas.getContext('2d'), viewport: viewport });

          // Rehber etiketi sayfalar oturunca ilk alana hizalansın
          if (index === this.pages.length - 1) this.$nextTick(() => this.alignGuide());
        });
      });
    },

    onResize() {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => {
        this.renderPages();
        this.alignGuide();
      }, 200);
    },

    refOf(name) {
      const ref = this.$refs[name];
      return Array.isArray(ref) ? ref[0] : ref;
    },

    // --- Alanlar -----------------------------------------------------------

    fieldsOnPage(page) {
      return this.fields.filter((f) => (f.page || 1) === page);
    },

    // Kaydırılmışsa yeni konum, değilse gönderenin koyduğu yer
    xOf(f) {
      const o = this.offsets[f.key];
      return o ? o.x : f.x;
    },
    yOf(f) {
      const o = this.offsets[f.key];
      return o ? o.y : f.y;
    },

    boxStyle(f) {
      return {
        left: (this.xOf(f) * 100) + '%',
        top: (this.yOf(f) * 100) + '%',
        width: (f.w * 100) + '%',
        height: (f.h * 100) + '%'
      };
    },

    isSignature(f) {
      return f.type === 'signature' || f.type === 'initials';
    },

    isFilled(f) {
      if (this.isSignature(f)) return !!this.signatureImage;
      if (f.type === 'checkbox') return !!this.values[f.key];
      return !!String(this.values[f.key] || '').trim();
    },

    fieldClass(f) {
      const pending = this.pendingFields.length ? this.pendingFields[0].key : '';
      return {
        'is-signature': this.isSignature(f),
        'is-filled': this.isFilled(f),
        'is-current': this.started && f.key === pending,
        'is-optional': f.required === false
      };
    },

    inputType(type) {
      return { date: 'date', number: 'number', email: 'email' }[type] || 'text';
    },

    touched() {
      this.$nextTick(() => this.alignGuide());
    },

    // --- İmza kutusunu kaydırma --------------------------------------------

    // Sunucudaki kuralın aynısı (signatureMove): ilk konumdan en fazla
    // `shift` kadar, sayfa dışına taşmadan. Ekranda da uygulanıyor ki
    // kullanıcı bırakınca kutu yerinden zıplamasın.
    clampAxis(value, origin, size) {
      const shift = this.signatureMove.shift;
      const lower = Math.max(origin - shift, 0);
      const upper = Math.min(origin + shift, 1 - size);
      return Math.min(Math.max(value, lower), upper);
    },

    pointOf(event) {
      const src = event.touches && event.touches.length ? event.touches[0]
        : (event.changedTouches && event.changedTouches.length ? event.changedTouches[0] : event);
      return { x: src.clientX, y: src.clientY };
    },

    startMove(event, f) {
      const box = this.refOf('field_' + f.key);
      const page = box ? box.parentElement : null;
      if (!page) return;

      const rect = page.getBoundingClientRect();
      const p = this.pointOf(event);
      this.moving = {
        field: f,
        startX: p.x,
        startY: p.y,
        originX: this.xOf(f),
        originY: this.yOf(f),
        pageWidth: rect.width,
        pageHeight: rect.height,
        moved: false
      };
      window.addEventListener('mousemove', this.onMove);
      window.addEventListener('mouseup', this.endMove);
      window.addEventListener('touchmove', this.onMove, { passive: false });
      window.addEventListener('touchend', this.endMove);
    },

    onMove(event) {
      const m = this.moving;
      if (!m) return;
      const p = this.pointOf(event);
      const dx = p.x - m.startX;
      // Yalnızca yatay kuralda dikey hareket hiç hesaba katılmıyor
      const dy = this.signatureMove.axis === 'xy' ? p.y - m.startY : 0;

      // Birkaç piksellik titreme sürükleme sayılmasın
      if (!m.moved && Math.abs(dx) < 4 && Math.abs(dy) < 4) return;
      m.moved = true;
      if (event.cancelable) event.preventDefault();

      const f = m.field;
      this.$set(this.offsets, f.key, {
        x: this.clampAxis(m.originX + dx / m.pageWidth, f.x, f.w || 0.2),
        y: this.clampAxis(m.originY + dy / m.pageHeight, f.y, f.h || 0.05)
      });
    },

    endMove() {
      const m = this.moving;
      window.removeEventListener('mousemove', this.onMove);
      window.removeEventListener('mouseup', this.endMove);
      window.removeEventListener('touchmove', this.onMove);
      window.removeEventListener('touchend', this.endMove);
      this.moving = null;
      if (!m) return;

      // Hareket yoksa bu bir tıklamaydı: imzayı değiştirme penceresi
      if (!m.moved) {
        this.openAdopt(m.field);
        return;
      }
      // İlk konumuna geri getirildiyse kayda gerek yok
      const o = this.offsets[m.field.key];
      if (o && Math.abs(o.x - m.field.x) < 0.001 && Math.abs(o.y - m.field.y) < 0.001) {
        this.$delete(this.offsets, m.field.key);
      }
      // Rehber etiketi kutunun yeni hizasına gelsin
      this.$nextTick(() => this.alignGuide());
    },

    // --- Rehber ------------------------------------------------------------

    // Etiketi sıradaki alanın hizasına taşır. Etiket belgeyle aynı kap
    // içinde, dolayısıyla belge kaydıkça onunla birlikte kayıyor.
    alignGuide() {
      const next = this.pendingFields[0];
      const pagesEl = this.$refs.pages;

      // Başlamadan önce: ilk sayfanın başı
      if (!this.started && pagesEl) {
        const first = this.refOf('page0');
        this.guideTop = first
          ? Math.max(first.getBoundingClientRect().top - pagesEl.getBoundingClientRect().top + 16, 0)
          : 16;
        return;
      }

      if (!next || !pagesEl) {
        this.guideTop = pagesEl ? Math.max(pagesEl.scrollHeight - 60, 16) : 16;
        return;
      }
      const box = this.refOf('field_' + next.key);
      if (!box) return;
      const pagesTop = pagesEl.getBoundingClientRect().top;
      const boxRect = box.getBoundingClientRect();
      this.guideTop = Math.max(boxRect.top - pagesTop + boxRect.height / 2 - 18, 0);
    },

    guide() {
      // Hepsi tamamsa gönder
      if (!this.remaining) {
        this.submit();
        return;
      }

      const next = this.pendingFields[0];
      const box = this.refOf('field_' + next.key);
      if (!box) return;

      // İlk tıklama: yalnızca alana götür. Etiket orada "İmzala" olur,
      // ikinci tıklamada işlem yapılır — kullanıcı nereye geldiğini görsün
      if (!this.started) {
        this.started = true;
        box.scrollIntoView({ behavior: 'smooth', block: 'center' });
        this.$nextTick(() => this.alignGuide());
        return;
      }

      // Hizadayken: imza alanıysa pencere, diğerlerinde imleç
      box.scrollIntoView({ behavior: 'smooth', block: 'center' });
      if (this.isSignature(next)) {
        this.openAdopt(next);
      } else {
        const input = box.querySelector('input, select');
        if (input) input.focus();
      }
    },

    // --- İmza penceresi ----------------------------------------------------

    closeAdopt() {
      signaturePad.stop();
      this.adoptOpen = false;
    },

    openAdopt() {
      this.adoptError = '';
      this.adoptOpen = true;
      if (!this.mode || (this.mode === 'saved' && !this.savedSignature)) this.mode = 'drawn';
      if (this.savedSignature && !this.signatureImage) this.mode = 'saved';
      this.$nextTick(() => {
        this.setupCanvas();
        this.renderTyped();
      });
    },

    adopt() {
      // Tablet görseli SigWeb'den asenkron geliyor
      if (this.isPadMode) {
        if (this.padState !== 'ready') return;
        signaturePad.capture()
          .then((result) => {
            // Cihaz kimliği denetim izine giriyor; simülatör ayrı ad veriyor
            this.signatureDevice = result.device;
            this.padCapture = result;
            signaturePad.stop();

            // Kalem verisi cihazdan çıkmadan mühürleniyor
            this.sealBiometric(result.sigString);
            this.applySignature(result.image, 'pad');
          })
          .catch(() => { this.adoptError = this.$t('sign.padEmpty'); });
        return;
      }

      const image = this.currentSignature();
      if (!image) {
        this.adoptError = this.$t('sign.errSignature');
        return;
      }
      this.signatureDevice = '';
      this.applySignature(image, this.mode === 'saved' ? 'drawn' : this.mode);
    },

    applySignature(image, method) {
      this.signatureImage = image;
      this.signatureType = method;
      this.adoptOpen = false;
      this.started = true;

      // Sıradaki alana geç
      this.$nextTick(() => {
        this.alignGuide();
        const next = this.pendingFields[0];
        if (next) {
          const box = this.refOf('field_' + next.key);
          if (box) box.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });
    },

    currentSignature() {
      if (this.mode === 'saved') return this.savedSignature;
      if (this.mode === 'uploaded') return this.uploadedImage;
      if (this.mode === 'drawn') {
        return this.hasStroke && this.$refs.pad ? this.$refs.pad.toDataURL('image/png') : '';
      }
      if (!String(this.typedName || '').trim() || !this.$refs.typedPad) return '';
      return this.$refs.typedPad.toDataURL('image/png');
    },

    setMode(mode) {
      // Tablet sekmesinden çıkarken dinlemeyi bırak: açık kalırsa
      // tablet başka bir uygulamaya verilemiyor
      if (this.isPadMode && mode !== this.mode) signaturePad.stop();

      this.mode = mode;
      this.adoptError = '';
      if (mode === 'saved' || mode === 'uploaded') return;
      if (mode === 'pad' || mode === 'padmock') {
        this.connectPad();
        return;
      }
      this.$nextTick(() => {
        if (mode === 'drawn') this.setupCanvas();
        else this.renderTyped();
      });
    },

    // --- İmza tableti ------------------------------------------------------

    connectPad() {
      const kind = this.padKind;
      this.padState = 'connecting';
      this.padError = '';
      signaturePad.detect(kind).then((result) => {
        if (!this.isPadMode || this.padKind !== kind) return;   // sekme değişmiş
        if (!result.ok) {
          this.padState = 'error';
          this.padError = result.reason;
          return;
        }
        this.padState = 'ready';
        this.padDevice = signaturePad.deviceInfo();
        this.$nextTick(() => signaturePad.start(this.$refs.padCanvas));
      });
    },

    // Biyometrik veriyi tarayıcıda şifreler. Şifrelenemiyorsa veri
    // gönderilmiyor: ham haliyle sunucuya çıkmasındansa hiç gitmesin.
    sealBiometric(sigString) {
      this.biometricSealed = null;
      if (!this.biometricCapture || !this.biometricKey || !sigString) return;

      if (!biometricSeal.isAvailable()) {
        console.warn('[imza] tarayıcı şifreleme desteklemiyor, biyometrik veri gönderilmeyecek');
        return;
      }

      biometricSeal.seal(sigString, this.biometricKey).then((sealed) => {
        this.biometricSealed = sealed;
        if (this.testMode) {
          console.log('[imza] biyometrik veri mühürlendi:',
            sealed ? sealed.size + ' karakter → ' + sealed.data.length + ' base64' : 'başarısız');
        }
      });
    },

    clearPad() {
      signaturePad.clear();
      const canvas = this.$refs.padCanvas;
      if (canvas) canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
    },

    // --- Yükleme -----------------------------------------------------------

    // Yüklenen görseli sunucunun kabul ettiği biçime getirir: 8 bit RGBA
    // PNG, makul boyut, beyaz zemin saydam. Taranmış veya fotoğrafı
    // çekilmiş imzalar beyaz kutu olarak belgeye basılmasın.
    onSignatureFile(event) {
      const file = event.target.files && event.target.files[0];
      event.target.value = '';
      if (!file) return;

      this.adoptError = '';
      if (['image/png', 'image/jpeg'].indexOf(file.type) === -1) {
        this.adoptError = this.$t('sign.uploadBadType');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        this.adoptError = this.$t('sign.uploadTooBig');
        return;
      }

      readBlobAsDataUrl(file).then((dataUrl) => {
        const img = new Image();
        img.onload = () => {
          const MAX_W = 800;
          const MAX_H = 300;
          const scale = Math.min(MAX_W / img.width, MAX_H / img.height, 1);
          const canvas = document.createElement('canvas');
          canvas.width = Math.max(Math.round(img.width * scale), 1);
          canvas.height = Math.max(Math.round(img.height * scale), 1);

          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

          // Açık renkli pikselleri saydamlaştır
          const data = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const px = data.data;
          for (let i = 0; i < px.length; i += 4) {
            const light = (px[i] + px[i + 1] + px[i + 2]) / 3;
            if (light > 225) px[i + 3] = 0;
          }
          ctx.putImageData(data, 0, 0);

          this.uploadedImage = canvas.toDataURL('image/png');
        };
        img.onerror = () => { this.adoptError = this.$t('sign.uploadBadType'); };
        img.src = dataUrl;
      });
    },

    setupCanvas() {
      const canvas = this.$refs.pad;
      if (!canvas) return;
      const ratio = window.devicePixelRatio || 1;
      canvas.width = canvas.offsetWidth * ratio;
      canvas.height = canvas.offsetHeight * ratio;
      this.ctx = canvas.getContext('2d');
      this.ctx.scale(ratio, ratio);
      this.ctx.lineWidth = 2.2;
      this.ctx.lineCap = 'round';
      this.ctx.lineJoin = 'round';
      this.ctx.strokeStyle = '#1a1a4b';
      this.hasStroke = false;
    },

    pointerPos(event) {
      const rect = this.$refs.pad.getBoundingClientRect();
      const source = event.touches && event.touches.length ? event.touches[0] : event;
      return { x: source.clientX - rect.left, y: source.clientY - rect.top };
    },

    startStroke(event) {
      if (!this.ctx) this.setupCanvas();
      this.isDrawing = true;
      const p = this.pointerPos(event);
      this.ctx.beginPath();
      this.ctx.moveTo(p.x, p.y);
    },
    moveStroke(event) {
      if (!this.isDrawing) return;
      const p = this.pointerPos(event);
      this.ctx.lineTo(p.x, p.y);
      this.ctx.stroke();
      this.hasStroke = true;
    },
    endStroke() {
      this.isDrawing = false;
    },

    clearSignature() {
      if (this.ctx && this.$refs.pad) {
        const canvas = this.$refs.pad;
        this.ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
      this.hasStroke = false;
    },

    renderTyped() {
      const canvas = this.$refs.typedPad;
      if (!canvas) return;
      const ratio = window.devicePixelRatio || 1;
      canvas.width = canvas.offsetWidth * ratio;
      canvas.height = canvas.offsetHeight * ratio;
      const ctx = canvas.getContext('2d');
      ctx.scale(ratio, ratio);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = '#1a1a4b';
      ctx.font = 'italic 40px "Brush Script MT", "Segoe Script", cursive';
      ctx.textBaseline = 'middle';
      ctx.textAlign = 'center';
      ctx.fillText(this.typedName || '', canvas.offsetWidth / 2, canvas.offsetHeight / 2);
    },

    loadSavedSignature() {
      api.get('/profile/signature', { responseType: 'blob' })
        .then((blob) => {
          if (!blob || (blob.type && blob.type.indexOf('image/png') === -1)) return;
          return readBlobAsDataUrl(blob).then((dataUrl) => {
            if (dataUrl) this.savedSignature = dataUrl;
          });
        })
        .catch(() => { /* kayıtlı imza yoksa çizerek devam edilir */ });
    },

    // --- Gönderme ----------------------------------------------------------

    submit() {
      if (this.saving) return;
      this.errorMessage = '';

      // Eksik varsa mesaj yerine doğrudan o alana götür
      if (this.remaining) {
        this.errorMessage = this.$t('sign.finishNeedsFields');
        const next = this.pendingFields[0];
        const box = this.refOf('field_' + next.key);
        this.started = true;
        if (box) box.scrollIntoView({ behavior: 'smooth', block: 'center' });
        this.$nextTick(() => this.alignGuide());
        return;
      }
      if (!this.consent) {
        this.errorMessage = this.$t('sign.finishNeedsConsent');
        return;
      }

      this.saving = true;

      // Sunucuya ne gittiği tarayıcıda da görünsün
      try {
        /* eslint-disable no-console */
        console.groupCollapsed('%c[imza] gönderiliyor', 'color:#0a7563; font-weight:700');
        console.log('yöntem:', this.signatureType);
        console.log('cihaz:', this.signatureDevice || '(yok)');
        console.log('taşınan alanlar:', this.offsets);
        if (this.padCapture) console.log('tabletten toplanan:', this.padCapture);
        console.log('biyometrik (mühürlü) gönderiliyor mu:', this.biometricSealed ? 'evet' : 'hayır');
        console.groupEnd();
        /* eslint-enable no-console */
      } catch (e) { /* konsol yoksa sorun değil */ }

      const values = {};
      this.fields.forEach((f) => {
        if (this.isSignature(f)) return;
        values[f.key] = f.type === 'checkbox' ? String(!!this.values[f.key]) : this.values[f.key];
      });

      const needsSignature = this.fields.some((f) => this.isSignature(f));

      api.post('/sign/' + this.token, {
        consent: true,
        values: values,
        signatureImage: needsSignature ? this.signatureImage : undefined,
        signatureType: this.signatureType,
        signatureDevice: this.signatureDevice || undefined,
        // Yalnızca MÜHÜRLÜ paket gidiyor; ham veri cihazdan çıkmıyor
        signatureBiometricSealed: this.biometricSealed || undefined,
        signatureBiometricStats: (this.biometricCapture && this.padCapture)
          ? this.padCapture.sigStats || undefined : undefined,
        // Yalnızca taşınan imza kutuları; sunucu sınırı yeniden uyguluyor
        fieldPositions: Object.keys(this.offsets).length ? this.offsets : undefined
      })
        .then((body) => {
          this.done = true;
          this.doneMessage = body.message;
        })
        .catch((err) => { this.errorMessage = err.message; })
        .then(() => { this.saving = false; });
    },

    decline() {
      if (this.saving) return;
      this.saving = true;
      api.post('/sign/' + this.token, { decline: true, reason: this.declineReason })
        .then((body) => {
          this.declineOpen = false;
          this.done = true;
          this.doneMessage = body.message;
        })
        .catch((err) => {
          this.declineOpen = false;
          this.errorMessage = err.message;
        })
        .then(() => { this.saving = false; });
    }
  }
};
</script>

<style scoped>
.sign-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: #e9ecf3;
}

/* --- Üst çubuk --- */
.topbar {
  position: sticky;
  top: 0;
  z-index: 30;
  display: flex;
  align-items: center;
  gap: 18px;
  padding: 10px 20px;
  background: #1c1c33;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.brand { display: flex; align-items: center; font-weight: 700; color: #fff; flex: 0 0 auto; }
.brand img { display: block; max-height: 28px; }

.top-mid { flex: 1 1 auto; min-width: 0; display: flex; align-items: center; gap: 14px; }

.top-subject {
  font-size: 14px;
  color: #e7e9f2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.top-progress {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #a66a12;
  background: #fdf2e2;
  border-radius: 999px;
  padding: 3px 11px;
}

.top-progress.is-done { color: #0f6e5c; background: #e4f6ef; }

.top-right { display: flex; align-items: center; gap: 12px; flex: 0 0 auto; }

/* --- Görüntüleyici --- */
.viewer {
  flex: 1 1 auto;
  padding: 22px 16px 120px;
}

.stage {
  position: relative;
  display: flex;
  gap: 14px;
  max-width: 1000px;
  margin: 0 auto;
}

/* Sol kenar: rehber etiketi burada, belgeyle birlikte kayıyor */
.rail {
  position: relative;
  flex: 0 0 92px;
}

.guide {
  position: absolute;
  right: 0;
  height: 36px;
  padding: 0 18px 0 14px;
  border: 0;
  border-radius: 6px 0 0 6px;
  background: #f5b82e;
  color: #3d2a05;
  font-size: 13.5px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 3px 10px rgba(120, 80, 0, 0.25);
  transition: top 0.35s ease, background-color 0.15s;
  white-space: nowrap;
}

/* Sağdaki ok ucu: etiket alanı işaret ediyor */
.guide::after {
  content: "";
  position: absolute;
  right: -14px;
  top: 0;
  border-top: 18px solid transparent;
  border-bottom: 18px solid transparent;
  border-left: 14px solid #f5b82e;
}

.guide:hover { background: #f7c448; }
.guide:hover::after { border-left-color: #f7c448; }

.guide.is-finish { background: #00b89c; color: #06302a; }
.guide.is-finish::after { border-left-color: #00b89c; }

.pages {
  position: relative;
  flex: 1 1 auto;
  min-width: 0;
}

.doc-note {
  background: #fff;
  border: 1px solid #e0e4ee;
  border-radius: 10px;
  padding: 12px 15px;
  font-size: 13.5px;
  color: #5a6180;
  margin-bottom: 14px;
  line-height: 1.6;
}

.doc-loading,
.doc-error {
  background: #fff;
  border-radius: 10px;
  padding: 44px 20px;
  text-align: center;
  color: #98a0b5;
}

.doc-error { color: #a8322c; }

.page {
  position: relative;
  width: 100%;
  height: 0;
  background: #fff;
  box-shadow: 0 2px 12px rgba(24, 24, 48, 0.14);
  margin-bottom: 18px;
}

.page-canvas {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}

/* --- Alanlar --- */
.field {
  position: absolute;
  display: flex;
  align-items: stretch;
  justify-content: stretch;
  border: 1.5px dashed #6f8bd0;
  background: rgba(111, 139, 208, 0.1);
  border-radius: 3px;
  transition: box-shadow 0.2s, border-color 0.2s, background-color 0.2s;
}

.field.is-signature { border-color: #e5a012; background: rgba(245, 184, 46, 0.14); }
.field.is-filled { border-style: solid; border-color: rgba(0, 184, 156, 0.55); background: rgba(0, 209, 178, 0.06); }
.field.is-optional { border-color: #b7bdcf; }

/* Sıradaki alan belirgin: kullanıcı nereye bakacağını arıyor */
.field.is-current {
  box-shadow: 0 0 0 3px rgba(245, 184, 46, 0.55);
  z-index: 3;
}

.sign-btn {
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border: 0;
  background: #f5b82e;
  color: #3d2a05;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}

.sign-btn:hover { background: #f7c448; }

.field-sig {
  width: 100%;
  height: 100%;
  object-fit: contain;
  cursor: pointer;
}

.field-input {
  flex: 1 1 auto;
  min-width: 0;
  border: 0;
  background: transparent;
  padding: 0 6px;
  font-size: 13px;
  color: #1e2233;
}

.field-input:focus { outline: 0; background: rgba(255, 255, 255, 0.8); }

.field-check {
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

/* --- Alt çubuk --- */
.bar {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 30;
  display: flex;
  align-items: center;
  gap: 18px;
  padding: 12px 22px;
  background: #fff;
  border-top: 1px solid #e0e4ee;
  box-shadow: 0 -6px 20px rgba(24, 24, 48, 0.08);
}

.consent {
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  gap: 11px;
  cursor: pointer;
  min-width: 0;
}

.consent input { position: absolute; opacity: 0; width: 0; height: 0; }

.switch-box {
  position: relative;
  flex: 0 0 38px;
  width: 38px;
  height: 21px;
  border-radius: 999px;
  background: #d9deea;
  transition: background-color 0.18s;
}

.switch-box::after {
  content: "";
  position: absolute;
  top: 2px;
  left: 2px;
  width: 17px;
  height: 17px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 3px rgba(20, 20, 40, 0.24);
  transition: transform 0.18s;
}

.consent input:checked + .switch-box { background: #00b89c; }
.consent input:checked + .switch-box::after { transform: translateX(17px); }

.consent-text { font-size: 12.5px; color: #4a5175; line-height: 1.5; }

/* --- Durum --- */
.state-wrap { flex: 1 1 auto; padding: 40px 20px; }

.state {
  max-width: 520px;
  margin: 0 auto;
  background: #fff;
  border-radius: 14px;
  padding: 50px 26px;
  text-align: center;
}

.state-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 60px;
  height: 60px;
  border-radius: 18px;
  font-size: 23px;
  margin-bottom: 15px;
}

.state-icon.is-ok { background: linear-gradient(140deg, #00d1b2 0%, #00a88f 100%); color: #06302a; }
.state-icon.is-idle { background: #f0f2f8; color: #a7aec1; }
.state h2 { font-size: 19px; font-weight: 700; color: #1e2233; }
.state p { font-size: 14px; color: #79809a; margin-top: 7px; line-height: 1.6; }

/* --- Bildirim --- */
.flash {
  position: sticky;
  top: 62px;
  z-index: 20;
  display: flex;
  align-items: center;
  gap: 10px;
  max-width: 1000px;
  margin: 0 auto 14px;
  border-radius: 10px;
  padding: 11px 14px;
  font-size: 13.5px;
  background: #fdeceb;
  color: #a8322c;
  box-shadow: 0 4px 14px rgba(160, 50, 45, 0.12);
}

.flash-close {
  margin-left: auto;
  background: transparent;
  border: 0;
  font-size: 20px;
  line-height: 1;
  color: inherit;
  opacity: 0.6;
  cursor: pointer;
}

/* --- Pencere --- */
.sheet {
  position: fixed;
  inset: 0;
  z-index: 60;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.sheet-backdrop { position: absolute; inset: 0; background: rgba(20, 20, 38, 0.55); backdrop-filter: blur(3px); }

.sheet-card {
  position: relative;
  width: 520px;
  max-width: 100%;
  max-height: 92vh;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 24px 60px rgba(16, 16, 36, 0.35);
  overflow: hidden;
}

.sheet-head {
  display: flex;
  align-items: flex-start;
  gap: 13px;
  padding: 20px 20px 16px;
  border-bottom: 1px solid #eef0f6;
}

.sheet-icon {
  flex: 0 0 40px;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: #f5b82e;
  color: #3d2a05;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
}

.sheet-icon.is-no { background: #fdeceb; color: #a8322c; }
.sheet-head-text { flex: 1 1 auto; min-width: 0; }
.sheet-head-text h3 { font-size: 16px; font-weight: 700; color: #1e2233; }
.sheet-head-text p { font-size: 12.5px; color: #8b93a8; margin-top: 4px; line-height: 1.55; }

.sheet-close { background: transparent; border: 0; font-size: 24px; line-height: 1; color: #b3bacb; cursor: pointer; }

.sheet-body { padding: 18px 20px 8px; overflow-y: auto; }

.sheet-foot {
  display: flex;
  gap: 8px;
  padding: 16px 20px;
  border-top: 1px solid #eef0f6;
  background: #fbfcfe;
}

.seg {
  display: inline-flex;
  background: #f2f4fa;
  border-radius: 10px;
  padding: 3px;
  gap: 2px;
  margin-bottom: 14px;
}

.seg button {
  background: transparent;
  border: 0;
  border-radius: 8px;
  padding: 7px 15px;
  font-size: 13px;
  font-weight: 600;
  color: #79809a;
  cursor: pointer;
}

.seg button.is-active { background: #fff; color: #0b6e5f; box-shadow: 0 1px 3px rgba(24, 24, 48, 0.14); }

.sig-paper {
  background: #fff;
  border: 1px solid #e6e8f0;
  border-radius: 11px;
  padding: 18px 18px 24px;
  text-align: center;
}

.sig-paper.is-pad { padding: 10px 10px 22px; }
.sig-paper img { max-height: 120px; max-width: 100%; }

.sig-rule { display: block; height: 1px; background: #dfe3ec; margin: 12px 34px 0; }

.pad {
  display: block;
  width: 100%;
  height: 170px;
  background: #fff;
  border: 1px dashed #cfd5e2;
  border-radius: 9px;
  cursor: crosshair;
  touch-action: none;
}

.pad.is-typed { height: 120px; border-style: solid; border-color: #eef0f6; cursor: default; margin-top: 10px; }

.sig-actions { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-top: 10px; }

.hint { font-size: 12.5px; color: #98a0b5; line-height: 1.6; }
.hint.center { text-align: center; margin-top: 10px; }

.err { display: flex; align-items: center; gap: 6px; font-size: 12.5px; color: #b63934; margin-top: 12px; }

.label { display: block; font-size: 12.5px; font-weight: 600; color: #5a6180; margin-bottom: 6px; }

.input {
  width: 100%;
  height: 42px;
  border: 1px solid #dde1ea;
  border-radius: 9px;
  padding: 0 13px;
  font-size: 14.5px;
  color: #2b3146;
  background: #fff;
}

.input.is-area { height: auto; padding: 11px 13px; line-height: 1.6; resize: vertical; }
.input.center-text { text-align: center; }

.input:focus {
  outline: 0;
  border-color: #00b89c;
  box-shadow: 0 0 0 3px rgba(0, 209, 178, 0.16);
}

/* --- Düğmeler --- */
.btn-primary,
.btn-ghost,
.btn-danger {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border-radius: 10px;
  padding: 11px 20px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}

.btn-primary {
  background: linear-gradient(135deg, #00d1b2 0%, #00a88f 100%);
  border: 1px solid transparent;
  color: #06302a;
  box-shadow: 0 4px 12px rgba(0, 168, 143, 0.3);
}

.btn-primary:hover { background: linear-gradient(135deg, #0ddcbe 0%, #00b79c 100%); }

/* Onay verilmemişken: tıklanabilir ama pasif görünüyor */
.btn-primary.btn-deactive,
.btn-primary.btn-deactive:hover {
  background: linear-gradient(135deg, #c3cad6 0%, #9aa3b3 100%);
  color: #2b3146;
  box-shadow: none;
}
.btn-primary.is-loading, .btn-danger.is-loading { opacity: 0.7; pointer-events: none; }

.btn-ghost { background: #fff; border: 1px solid #dde1ea; color: #3c4257; padding: 10px 17px; font-size: 13.5px; }
.btn-ghost.is-mini { padding: 6px 12px; font-size: 12.5px; }

.btn-danger { background: #c4413c; border: 1px solid #c4413c; color: #fff; }
.btn-danger:hover { background: #ad3833; }

.btn-link {
  background: transparent;
  border: 0;
  color: #8b93a8;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.btn-link:hover { color: #f08a84; }
.topbar .btn-link { color: #aeb4cc; }
.topbar .LanguageSwitcher, .top-right { color: #fff; }

.page-foot {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex-wrap: wrap;
  padding: 22px 20px 30px;
  font-size: 12.5px;
  color: #98a0b5;
}

.page-foot a { color: #0b6e5f; font-weight: 600; }

/* --- İmza yükleme --- */
.drop {
  border: 2px dashed #d5dae6;
  border-radius: 12px;
  min-height: 150px;
  padding: 20px 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  cursor: pointer;
  background: #fbfcfe;
  transition: border-color 0.15s, background-color 0.15s;
}

.drop:hover { border-color: #00b89c; background: #f2fcfa; }
.drop.has-file { border-style: solid; border-color: #bfe8df; background: #fff; }

.hidden-input { display: none; }

.drop-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 13px;
  background: #eef1f8;
  color: #8b93a8;
  font-size: 18px;
  margin-bottom: 9px;
}

.drop-text { font-size: 13.5px; color: #5a6180; }

/* Saydam zeminli imza: dama deseni saydamlığın işe yaradığını gösteriyor */
.drop-preview {
  max-width: 100%;
  max-height: 130px;
  background-image:
    linear-gradient(45deg, #f1f3f8 25%, transparent 25%),
    linear-gradient(-45deg, #f1f3f8 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, #f1f3f8 75%),
    linear-gradient(-45deg, transparent 75%, #f1f3f8 75%);
  background-size: 14px 14px;
  background-position: 0 0, 0 7px, 7px -7px, -7px 0;
  border-radius: 6px;
}

/* --- İmza tableti --- */
.pad-status {
  display: flex;
  align-items: center;
  gap: 9px;
  border-radius: 10px;
  padding: 11px 13px;
  font-size: 13px;
  line-height: 1.5;
  margin-bottom: 12px;
  background: #f4f6fb;
  color: #5a6180;
}

.pad-status .btn-ghost { margin-left: auto; }
.pad-status.is-ready { background: #e4f6ef; color: #0f6e5c; }
.pad-status.is-error { background: #fdeceb; color: #a8322c; }

/* Tablet çizimi yansıtılıyor, fareyle çizilmiyor */
.pad.is-hardware {
  height: 170px;
  border-style: solid;
  border-color: #eef0f6;
  cursor: default;
}

/* Beş sekme dar ekranda sığsın */
.seg { flex-wrap: wrap; }

.note {
  display: flex;
  align-items: flex-start;
  gap: 9px;
  border-radius: 10px;
  padding: 11px 13px;
  font-size: 12.5px;
  line-height: 1.55;
  margin-top: 12px;
}

.note.is-info { background: #eaf1fd; color: #24508f; }
.note i { margin-top: 2px; }

/* --- Taşınabilir imza --- */
.field-sig-wrap {
  position: relative;
  flex: 1 1 auto;
  display: flex;
  cursor: grab;
  touch-action: none;
  user-select: none;
}

.field-sig-wrap:active { cursor: grabbing; }

.field-sig-wrap .field-sig { pointer-events: none; }

/* Taşınabildiğini gösteren küçük tutamak: üzerine gelince beliriyor */
.move-hint {
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  width: 26px;
  height: 20px;
  border-radius: 10px;
  background: #1c1c33;
  color: #fff;
  font-size: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.15s;
  pointer-events: none;
}

.field:hover .move-hint,
.field-sig-wrap:active .move-hint { opacity: 1; }

/* Dokunmatik ekranda hover yok: tutamak hep görünsün */
@media (hover: none) {
  .move-hint { opacity: 0.85; }
}

@media screen and (max-width: 720px) {
  .top-subject { display: none; }
  .viewer { padding: 14px 8px 150px; }
  .stage { gap: 6px; }
  .rail { flex: 0 0 64px; }
  .guide { padding: 0 12px 0 10px; font-size: 12px; }
  .bar { flex-direction: column; align-items: stretch; gap: 10px; }
  .bar .btn-primary { justify-content: center; }
  .sheet { padding: 0; align-items: flex-end; }
  .sheet-card { width: 100%; border-radius: 16px 16px 0 0; }
}
</style>


// WEBPACK FOOTER //
// src/Components/Sign.vue